import React from 'react';
import { Box } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Container, Section, SectionTitle, TelegramCta } from './primitives';
import { landing } from './tokens';

/**
 * The price and the founders offer, with its rules spelled out: who counts
 * among the 50, when the free months start, and that nothing is charged
 * without notice. No testimonials until real ones exist.
 */
const PricingSection: React.FC = () => {
  const { t } = useTranslation('landing');
  const rules = t('offer.items', { returnObjects: true }) as unknown as string[];
  const features = t('pricing.features', { returnObjects: true }) as unknown as string[];
  return (
    <Section sx={{ bgcolor: landing.bg2 }}>
      <Container sx={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'stretch' }}>
        <Box
          sx={{
            flex: '1 1 420px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <SectionTitle sx={{ fontSize: 'clamp(30px, 3.4vw, 44px)', lineHeight: 1.06 }}>
            {t('offer.title')}
          </SectionTitle>
          <Box
            component='ol'
            sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {rules.map((rule, index) => (
              <Box
                key={rule}
                component='li'
                sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start', fontSize: 17, lineHeight: 1.55 }}
              >
                <Box
                  component='span'
                  aria-hidden='true'
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 700,
                    bgcolor: landing.accentSoft,
                    color: landing.accent,
                  }}
                >
                  {index + 1}
                </Box>
                <Box component='span' sx={{ pt: '3px' }}>
                  {rule}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            flex: '1 1 360px',
            minWidth: 0,
            maxWidth: { md: 460 },
            p: 4,
            borderRadius: '28px',
            bgcolor: landing.surface,
            border: `1px solid ${landing.line}`,
            boxShadow: landing.shadow,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box
              component='span'
              sx={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: landing.accent,
              }}
            >
              {t('pricing.eyebrow')}
            </Box>
            <Box
              component='span'
              sx={{
                fontFamily: landing.display,
                fontSize: 52,
                fontWeight: 750,
                letterSpacing: '-0.035em',
                lineHeight: 1.05,
              }}
            >
              {t('pricing.price')}
            </Box>
            <Box component='span' sx={{ fontSize: 15, lineHeight: 1.55, color: landing.ink2 }}>
              {t('pricing.condition')}
            </Box>
          </Box>
          <Box
            component='ul'
            sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}
          >
            {features.map((feature) => (
              <Box
                key={feature}
                component='li'
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  fontSize: 15,
                  '& svg': { fontSize: 19, color: landing.accent, flexShrink: 0, mt: '1px' },
                }}
              >
                <CheckIcon aria-hidden='true' />
                {feature}
              </Box>
            ))}
          </Box>
          <TelegramCta label={t('pricing.cta')} size='md' icon={false} sx={{ mt: 'auto' }} />
        </Box>
      </Container>
    </Section>
  );
};

export default PricingSection;
